import * as React from 'react';
import { LoadableProvider } from 'Components/LoadableProvider';
import { LaunchResourceOptions } from 'Environment';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import {
	InstalledResource,
	Resource,
	subscriptionStatus,
} from 'Workspace/ResourceProvider/resourceTypes';

export interface HTML5TroubleshootPayload {
	resourceId: string;
	errorId: string;
	correlationId: string;
}

/**
 * It is used to track the progress of a troubleshoot fix operation.
 * Pending: The troubleshoot fix is pending and has not yet started.
 * Success: The troubleshoot fix has completed successfully.
 * InProgress: The troubleshoot fix is currently in progress.
 * Failed: The troubleshoot fix has failed.
 * NOT_APPLICABLE: The troubleshoot fix is not applicable.
 */
export enum TroubleshootFixStatus {
	NOT_APPLICABLE = 'not-applicable',
	NOT_STARTED = 'not-started',
	SUCCEEDED = 'succeeded',
	FAILED = 'failed',
	IN_PROGRESS = 'in-progress',
}

export type LaunchProgressDetails = {
	resourceId: string;
	status: LaunchStatus;
	onCancel?: () => void;
	error?: string;
	troubleshoot?: boolean;
	timeStamp?: number;
	troubleshootFixStatus?: TroubleshootFixStatus;
};
export type ResourceLaunchProgressMap = {
	[key: string]: LaunchProgressDetails;
};
type UpdateSession = (currentSession: ResourceContext) => Partial<ResourceContext>;
type UpdateSessionFunction = (
	callbackOrData: UpdateSession | Partial<ResourceContext>
) => void;

export interface ResourceLatestStatusEntry
	extends Pick<
		LaunchProgressDetails,
		| 'resourceId'
		| 'status'
		| 'error'
		| 'timeStamp'
		| 'troubleshootFixStatus'
		| 'troubleshoot'
	> {
	operationId: string;
}

export interface ResourceLatestStatusMap {
	[resourceId: string]: ResourceLatestStatusEntry;
}

export interface ResourceContext {
	isUnauthenticatedStore: boolean;
	resources: Resource[];
	resourceLatestStatus: ResourceLatestStatusMap;
	installedResources: InstalledResource[];
	launchInProgressIds: string[];
	launchProgress: ResourceLaunchProgressMap;
	desktopRestartInProgressIds: string[];
	preferLeaseLaunchIds: string[];
	setLoading(loading: boolean): void;
	updateSession: UpdateSessionFunction;
	setSubscriptionStatus(resourceId: string, subscriptionStatus: subscriptionStatus): void;
	setSiriRegistrationStatus(resourceId: string, status: boolean): void;
	makeResourceMostRecent(resourceId: string): void;
	startRecordingResourceUpdates(): void;
	stopRecordingResourceUpdates(): void;
	applyRecordedResourceUpdates(resources: Resource[]): Resource[];
	isInstalled(id: string): boolean;
	isUpgradeAvailable(id: string): boolean;
	isInProgress(id: string): boolean;
	subscriptionsEnabled(): boolean;
	tryLaunchViaBrowserExtension(
		launchResourceOptions: LaunchResourceOptions
	): Promise<any>;
	tryLaunchViaChromeApp(launchResourceOptions: LaunchResourceOptions): Promise<any>;
	shouldBrowserExtensionLaunchResource(resource: Resource): Promise<boolean>;
	shouldChromeAppLaunchResource(resource: Resource): Promise<boolean>;
	setMultipleResourceSubscriptionStatus(
		resourceIds: string[],
		subscriptionStatus: subscriptionStatus
	): void;
	updateResourceTroubleshootFixStatus(
		resourceId: string,
		troubleshootFixStatus: TroubleshootFixStatus
	): void;
	troubleshootQueue: HTML5TroubleshootPayload[];
	clearPendingTroubleshoot(): void;
	filesSSOUrl: string;
	isNetworkValidatedEnumeration?: boolean;
	disallowICADownload?: boolean;
	citrixBrowserResourceID?: string;
	isSubscriptionEnabled?: boolean;
	devicePosture?: DevicePosture;
	serverName?: string | null;
	isDetailedDesktopTileEnabled?: boolean;
}

export interface DevicePosture {
	state: string;
	scanTransactionId: string;
	scanTime?: string;
	errorCode?: string;
}

export type ResourceContextProvider = LoadableProvider<ResourceContext>;
export const LoadableResourceContext = React.createContext<ResourceContextProvider>({
	loading: true,
	value: null,
});
